import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { toast } from "sonner";
import { Plus, X, Loader2, Inbox, Copy, Trash2 } from "lucide-react";
import EmailStatusBadge from "../components/EmailStatusBadge";

function fmtDate(iso) { if (!iso) return ""; return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }

export default function FileRequests() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const reload = async () => {
    setLoading(true);
    const [r, c] = await Promise.all([
      axios.get(`${API_BASE}/file-requests`),
      axios.get(`${API_BASE}/clients`),
    ]);
    setRows(r.data); setClients(c.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "—";

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="file-requests-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Documents</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>File requests</h1>
          <p className="text-[var(--ink-2)] mt-2">Ask clients for documents — emailed with a magic link or copyable URL.</p>
        </div>
        <button onClick={() => setShow(true)} className="btn-primary" data-testid="file-request-create-btn">
          <Plus size={14} /> New request
        </button>
      </div>

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="file-requests-empty">
          <Inbox size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No file requests yet</div>
          <div className="text-[var(--ink-3)] text-sm">Create one to start gathering documents from clients.</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>Title</th><th>Client</th><th>Items</th><th>Status</th><th>Email</th><th>Created</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const required = r.items.filter((i) => i.required !== false);
                const done = required.filter((i) => i.document_id).length;
                return (
                  <tr key={r.file_request_id} data-testid={`file-request-row-${r.file_request_id}`}>
                    <td className="font-medium">{r.title}</td>
                    <td>{clientName(r.client_id)}</td>
                    <td className="font-mono text-[12px]">{done}/{required.length}</td>
                    <td>
                      <span className="badge" style={{
                        background: r.status === "completed" ? "var(--moss)" : "var(--accent)", color: "var(--paper)"
                      }}>{r.status}</span>
                    </td>
                    <td><EmailStatusBadge status={r.last_email_status} testid={`file-request-email-status-${r.file_request_id}`} /></td>
                    <td className="text-[12px] text-[var(--ink-3)] font-mono">{fmtDate(r.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {show && (
        <FileRequestModal clients={clients} onClose={() => setShow(false)} onCreated={() => { setShow(false); reload(); }} />
      )}
    </div>
  );
}

function FileRequestModal({ clients, onClose, onCreated }) {
  const [form, setForm] = useState({ client_id: clients[0]?.client_id || "", title: "", description: "", due_at: "" });
  const _iuid = () => `it_${Math.random().toString(36).slice(2, 9)}`;
  const [items, setItems] = useState([
    { _uid: _iuid(), name: "Bank statements", required: true },
    { _uid: _iuid(), name: "W-9 form", required: true },
  ]);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const addItem = () => setItems((xs) => [...xs, { _uid: _iuid(), name: "", required: true }]);
  const updItem = (i, k, v) => setItems((xs) => xs.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmItem = (i) => setItems((xs) => xs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.title || items.filter((i) => i.name.trim()).length === 0) {
      setErr("Pick a client, title, and at least 1 item."); return;
    }
    setBusy(true); setErr("");
    try {
      const payload = {
        client_id: form.client_id, title: form.title, description: form.description || undefined,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : undefined,
        items: items.filter((i) => i.name.trim()).map((i) => ({ name: i.name.trim(), required: i.required })),
      };
      const r = await axios.post(`${API_BASE}/file-requests`, payload);
      setResult(r.data);
      toast.success("File request created");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    const link = result?.client_link?.link;
    if (!link) return;
    const full = link.startsWith("http") ? link : window.location.origin + link;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Link copied");
    } catch { toast.error("Copy failed — link: " + full); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(26,24,20,0.45)" }} data-testid="file-request-modal">
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">New file request</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Request documents</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="file-request-modal-close"><X size={20} /></button>
        </div>

        {result ? (
          <div data-testid="file-request-result">
            <div className="text-[14px] mb-4">File request <b>{result.file_request.title}</b> created.</div>
            <div className="card p-4 mb-4" style={{ background: "var(--subtle)" }}>
              <div className="overline mb-1">Client access link</div>
              <div className="text-[12px] font-mono break-all">{result.client_link?.link ? (result.client_link.link.startsWith("http") ? result.client_link.link : window.location.origin + result.client_link.link) : "No client portal user yet — invite them on the Clients page."}</div>
              {result.client_link?.email_result?.ok === false && (
                <div className="text-[12px] text-[var(--accent)] mt-2">
                  Email could not be delivered (Resend test mode — only the verified address receives mail).
                  Copy the link below to share manually.
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={copyLink} className="btn-secondary flex-1 justify-center" data-testid="file-request-copy-link">
                <Copy size={12} /> Copy link
              </button>
              <button onClick={onCreated} className="btn-primary flex-1 justify-center" data-testid="file-request-done">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="file-request-form">
            <Select label="Client" value={form.client_id} onChange={upd("client_id")} testid="file-request-client">
              {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
            </Select>
            <Field label="Title" value={form.title} onChange={upd("title")} testid="file-request-title" required />
            <Field label="Description (optional)" value={form.description} onChange={upd("description")} testid="file-request-description" />
            <Field label="Due date (optional)" type="date" value={form.due_at} onChange={upd("due_at")} testid="file-request-due" />

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Items requested</label>
                <button type="button" onClick={addItem} className="text-[12px] text-[var(--accent)] hover:underline" data-testid="file-request-add-item">+ Add</button>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={it._uid} className="flex items-center gap-2" data-testid={`file-request-item-${i}`}>
                    <input
                      value={it.name}
                      onChange={(e) => updItem(i, "name", e.target.value)}
                      placeholder={`Item ${i + 1}`}
                      className="flex-1 bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]"
                    />
                    <label className="inline-flex items-center gap-1 text-[12px] text-[var(--ink-3)]">
                      <input type="checkbox" checked={it.required} onChange={(e) => updItem(i, "required", e.target.checked)} /> required
                    </label>
                    <button type="button" onClick={() => rmItem(i)} className="text-[var(--ink-3)] hover:text-[var(--accent)]">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {err && <div className="text-[13px] text-[var(--accent)]" data-testid="file-request-error">{err}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center" data-testid="file-request-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}{busy ? "Creating…" : "Create & send"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
    </div>
  );
}
function Select({ label, value, onChange, children, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]">
        {children}
      </select>
    </div>
  );
}
